import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSite } from '../../context/SiteContext';
import { useProducts } from '../../context/ProductContext';
import { isRedirectShortlinkPath } from '../../utils/redirectShortlink';
import './EngagementPopups.css';

/** Static site popups — independent of admin Firestore popup/redirect settings */
const POPUP_DELAY = {
  onLoad: 2000,
  between: 3500,
  repeatCycle: 4000,
};
const EXIT_MS = 420;
const NEWSLETTER_KEY = 'rl_newsletter_subscribers';

function pickFeaturedProduct(products) {
  if (!products?.length) return null;
  const onSale = products.find((p) => p.badge === 'SALE' && p.salePrice);
  const bestSeller = products.find((p) => p.badge === 'NEW');
  const topRated = [...products].sort((a, b) => (b.rating || 0) - (a.rating || 0))[0];
  return onSale || bestSeller || topRated || products[0];
}

function getNextPopupType(current, hasFeatured) {
  if (!current || current === 'newsletter') return 'feature';
  if (current === 'feature') return hasFeatured ? 'product' : 'newsletter';
  if (current === 'product') return 'newsletter';
  return 'feature';
}

function getDelayAfter(current) {
  return current === 'newsletter' ? POPUP_DELAY.repeatCycle : POPUP_DELAY.between;
}

function formatPrice(p) {
  const price = p.salePrice || p.price;
  return `$${Number(price).toFixed(2)}`;
}

function originalPrice(p) {
  return p.salePrice ? `$${Number(p.price).toFixed(2)}` : null;
}

export default function EngagementPopups() {
  const navigate = useNavigate();
  const location = useLocation();
  const { siteName } = useSite();
  const { products } = useProducts();

  const [activeType, setActiveType] = useState(null);
  const [phase, setPhase] = useState('hidden');
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const timers = useRef({ load: null, chain: null, exit: null });

  const featured = useMemo(() => pickFeaturedProduct(products), [products]);

  const clearTimer = useCallback((key) => {
    const id = timers.current[key];
    if (id) {
      window.clearTimeout(id);
      timers.current[key] = null;
    }
  }, []);

  const clearAll = useCallback(() => {
    Object.keys(timers.current).forEach((key) => clearTimer(key));
  }, [clearTimer]);

  const canShow = useMemo(() => {
    if (location.pathname.includes('/admin')) return false;
    if (isRedirectShortlinkPath(location.pathname)) return false;
    return true;
  }, [location.pathname]);

  const resolvePopupType = useCallback(
    (type) => {
      if (type === 'product' && !featured) return 'newsletter';
      return type;
    },
    [featured]
  );

  const openPopup = useCallback(
    (type) => {
      const resolved = resolvePopupType(type);
      if (resolved === 'newsletter') setSubscribed(false);
      setActiveType(resolved);
      setPhase('enter');
    },
    [resolvePopupType]
  );

  const scheduleNext = useCallback(
    (currentType) => {
      clearTimer('chain');
      const next = getNextPopupType(currentType, featured);
      const delay = getDelayAfter(currentType);
      timers.current.chain = window.setTimeout(() => {
        timers.current.chain = null;
        openPopup(next);
      }, delay);
    },
    [featured, openPopup, clearTimer]
  );

  const dismiss = useCallback(
    (type) => {
      clearTimer('chain');
      clearTimer('exit');
      setPhase('exit');
      timers.current.exit = window.setTimeout(() => {
        timers.current.exit = null;
        setPhase('hidden');
        setActiveType(null);
        scheduleNext(type);
      }, EXIT_MS);
    },
    [scheduleNext, clearTimer]
  );

  const openPopupRef = useRef(openPopup);
  openPopupRef.current = openPopup;

  useEffect(() => {
    if (activeType !== 'product' || featured || phase === 'hidden') return undefined;
    dismiss('product');
    return undefined;
  }, [activeType, featured, phase, dismiss]);

  useEffect(() => {
    if (!canShow) {
      clearAll();
      setActiveType(null);
      setPhase('hidden');
      return undefined;
    }
    clearTimer('load');
    timers.current.load = window.setTimeout(() => {
      timers.current.load = null;
      openPopupRef.current('feature');
    }, POPUP_DELAY.onLoad);
    return () => clearTimer('load');
  }, [canShow, clearTimer, clearAll]);

  useEffect(() => () => clearAll(), [clearAll]);

  const closeFeature = useCallback(() => dismiss('feature'), [dismiss]);
  const closeProduct = useCallback(() => dismiss('product'), [dismiss]);
  const closeNewsletter = useCallback(() => dismiss('newsletter'), [dismiss]);

  const goToProduct = useCallback(() => {
    if (!featured) return;
    closeProduct();
    navigate(`/product/${featured.id}`);
  }, [featured, closeProduct, navigate]);

  const handleSubscribe = (e) => {
    e.preventDefault();
    const val = email.trim();
    if (!val || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return;
    try {
      const existing = JSON.parse(localStorage.getItem(NEWSLETTER_KEY) || '[]');
      if (!existing.includes(val)) {
        localStorage.setItem(NEWSLETTER_KEY, JSON.stringify([...existing, val]));
      }
    } catch { /* ignore */ }
    setSubscribed(true);
    window.setTimeout(() => closeNewsletter(), 2200);
  };

  if (!canShow || !activeType || phase === 'hidden') return null;

  const cls = phase === 'exit' ? 'is-exit' : 'is-enter';

  /* ── Feature center modal ── */
  if (activeType === 'feature') {
    return (
      <div
        className={`rl-popup rl-popup--center ${cls}`}
        role="presentation"
        onMouseDown={(e) => e.target === e.currentTarget && closeFeature()}
      >
        <div className="rl-popup__scrim" aria-hidden />
        <div className="rl-popup__card rl-popup__card--feature" role="dialog" aria-modal="true" aria-labelledby="rl-feat-title">
          <div className="rl-popup__card-top-bar" aria-hidden />
          <button className="rl-popup__close" aria-label="Close" onClick={closeFeature}>✕</button>

          <div className="rl-popup__feat-eyebrow rl-stagger" style={{ '--i': 0 }}>
            <span className="rl-popup__star-icon" aria-hidden>★</span>
            <span>Limited-time event</span>
          </div>

          <h2 id="rl-feat-title" className="rl-popup__feat-title rl-stagger" style={{ '--i': 1 }}>
            Up to 60% off<br /><em>{siteName}</em> favorites
          </h2>

          <p className="rl-popup__feat-lead rl-stagger" style={{ '--i': 2 }}>
            Thousands of styles across Women, Men, Home &amp; Beauty — from top brands, hand-picked for you.
          </p>

          <ul className="rl-popup__feat-chips rl-stagger" style={{ '--i': 3 }}>
            <li className="rl-popup__chip">
              <span className="rl-popup__chip-icon">👗</span>
              <span>Designer fashion</span>
            </li>
            <li className="rl-popup__chip">
              <span className="rl-popup__chip-icon">🏠</span>
              <span>Home & gifts</span>
            </li>
            <li className="rl-popup__chip">
              <span className="rl-popup__chip-icon">✨</span>
              <span>Beauty &amp; fragrance</span>
            </li>
          </ul>

          <div className="rl-popup__feat-actions rl-stagger" style={{ '--i': 4 }}>
            <button
              className="rl-popup__btn rl-popup__btn--primary"
              onClick={() => { closeFeature(); navigate('/shop'); }}
            >
              Shop the sale →
            </button>
            <button className="rl-popup__btn rl-popup__btn--ghost" onClick={closeFeature}>
              Maybe later
            </button>
          </div>

          <p className="rl-popup__feat-fine rl-stagger" style={{ '--i': 5 }}>
            ★ Star Rewards members earn extra points on every purchase.
          </p>
        </div>
      </div>
    );
  }

  /* ── Product bottom-left toast ── */
  if (activeType === 'product' && featured) {
    const orig = originalPrice(featured);
    return (
      <aside className={`rl-popup rl-popup--product ${cls}`} aria-live="polite">
        <div className="rl-popup__card rl-popup__card--product" role="dialog" aria-modal="true">
          <div className="rl-popup__prod-bar" aria-hidden />
          <button className="rl-popup__close rl-popup__close--sm" aria-label="Close" onClick={closeProduct}>✕</button>

          <span className="rl-popup__prod-label rl-stagger" style={{ '--i': 0 }}>
            {featured.badge || 'Featured'}
          </span>

          <div className="rl-popup__prod-inner rl-stagger" style={{ '--i': 1 }}>
            <img
              src={featured.image}
              alt={featured.name}
              className="rl-popup__prod-img"
            />
            <div className="rl-popup__prod-body">
              <p className="rl-popup__prod-brand">{featured.brand}</p>
              <h3 className="rl-popup__prod-name">{featured.name}</h3>
              <div className="rl-popup__prod-pricing">
                <span className="rl-popup__prod-price">{formatPrice(featured)}</span>
                {orig && <span className="rl-popup__prod-orig">{orig}</span>}
              </div>
              <div className="rl-popup__prod-rating">
                {'★'.repeat(Math.round(featured.rating || 0))}
                {'☆'.repeat(5 - Math.round(featured.rating || 0))}
                <span>({featured.reviews?.toLocaleString()})</span>
              </div>
            </div>
          </div>

          <div className="rl-popup__prod-actions rl-stagger" style={{ '--i': 2 }}>
            <button className="rl-popup__btn rl-popup__btn--primary rl-popup__btn--block" onClick={goToProduct}>
              View product →
            </button>
            <button className="rl-popup__btn rl-popup__btn--ghost rl-popup__btn--block" onClick={closeProduct}>
              Not now
            </button>
          </div>
        </div>
      </aside>
    );
  }

  /* ── Newsletter bottom-right toast ── */
  if (activeType === 'newsletter') {
    return (
      <aside className={`rl-popup rl-popup--newsletter ${cls}`} aria-live="polite">
        <div className="rl-popup__card rl-popup__card--newsletter">
          <div className="rl-popup__nl-topbar" aria-hidden />
          <button className="rl-popup__close rl-popup__close--sm" aria-label="Close" onClick={closeNewsletter}>✕</button>

          {subscribed ? (
            <div className="rl-popup__nl-success rl-stagger" style={{ '--i': 0 }}>
              <span className="rl-popup__nl-check" aria-hidden>✓</span>
              <div>
                <strong>You're in!</strong>
                <p>Exclusive deals and new arrivals are on their way.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="rl-popup__nl-head rl-stagger" style={{ '--i': 0 }}>
                <span className="rl-popup__nl-star" aria-hidden>✉</span>
                <span className="rl-popup__nl-badge">Members only</span>
              </div>
              <h3 className="rl-popup__nl-title rl-stagger" style={{ '--i': 1 }}>
                Get 15% off your first order
              </h3>
              <p className="rl-popup__nl-text rl-stagger" style={{ '--i': 2 }}>
                Join our list for early access to sales, new arrivals &amp; exclusive offers.
              </p>
              <form className="rl-popup__nl-form rl-stagger" style={{ '--i': 3 }} onSubmit={handleSubscribe}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  aria-label="Email address"
                  required
                />
                <button type="submit" className="rl-popup__btn rl-popup__btn--primary">
                  Subscribe
                </button>
              </form>
              <p className="rl-popup__nl-fine rl-stagger" style={{ '--i': 4 }}>
                Unsubscribe anytime. No spam, ever.
              </p>
            </>
          )}
        </div>
      </aside>
    );
  }

  return null;
}
